import React, { useCallback, useMemo } from "react";
import { IdPClientRoot, useIdPClient } from "./client";
import { IDP_CALLBACK_PATH } from "./constants";
import { IdPAuthContext, IdPAuthContextInterface } from "./IdPAuthContext";
import { auth0Issuer, IdPIssuerConfig } from "./issuers";
import {
  AppState,
  IdPLogoutOptions,
  IdPRedirectError,
  IdPUser,
  LoginWithIdPOptions,
} from "./types";
import { usePrepareLogin } from "./usePrepareLogin";

export interface IdPAuthProviderOptions {
  children: React.ReactNode;
  context?: React.Context<IdPAuthContextInterface>;

  // IdP tenant info
  domain: string;
  clientId: string;
  audience: string;

  // Proxy Domain info for Authorize call
  proxyDomainForAuthorize?: string;

  /**
   * Issuers trusted after the Auth0 tenant. Every deployed environment trusts Auth0 only.
   */
  additionalIssuers?: IdPIssuerConfig[];

  /**
   * Called once the IdP client has handled the callback route.
   */
  onRedirectCallback: (appState?: AppState, user?: IdPUser) => void;

  /**
   * Called once when the callback route carries an error, or the IdP client could not complete
   * the callback.
   */
  onRedirectError: (error: IdPRedirectError) => void;
}

const callbackUrl = () =>
  (typeof window !== "undefined" && `${window.location.origin}${IDP_CALLBACK_PATH}`) || "";

/**
 * Mounts the IdP client and provides the IdP session through IdPAuthContext.
 * Sibling of SendGridSessionProvider: this one owns the IdP half of a unified login.
 */
export const IdPAuthProvider = ({
  children,
  context,
  domain,
  clientId,
  audience,
  proxyDomainForAuthorize,
  additionalIssuers,
  onRedirectCallback,
  onRedirectError,
}: IdPAuthProviderOptions): JSX.Element => {
  const redirectUri = callbackUrl();
  const issuers = useMemo(
    () => [
      auth0Issuer({ domain, clientId, proxyDomainForAuthorize }),
      ...(additionalIssuers ?? []),
    ],
    [domain, clientId, proxyDomainForAuthorize, additionalIssuers]
  );
  return (
    <IdPClientRoot
      issuers={issuers}
      audience={audience}
      redirectUri={redirectUri}
      onRedirectCallback={onRedirectCallback}
      onRedirectError={onRedirectError}>
      <IdPAuthProviderImpl context={context} audience={audience}>
        {children}
      </IdPAuthProviderImpl>
    </IdPClientRoot>
  );
};

interface IdPAuthProviderImplOptions {
  children: React.ReactNode;
  context?: React.Context<IdPAuthContextInterface>;
  audience: string;
}

const IdPAuthProviderImpl = ({
  children,
  context = IdPAuthContext,
  audience,
}: IdPAuthProviderImplOptions): JSX.Element => {
  const { client, isAuthenticated, isLoading, user } = useIdPClient();
  const prepareLogin = usePrepareLogin(audience);

  const loginWithRedirect = useCallback(
    async (opts: LoginWithIdPOptions = {}) => {
      await client.loginWithRedirect(await prepareLogin(opts));
    },
    [client, prepareLogin]
  );

  const getHeldTokens = useCallback(() => client.getHeldTokens(), [client]);

  const logout = useCallback((opts: IdPLogoutOptions) => client.logout(opts), [client]);

  const contextValue = useMemo(() => {
    return {
      isAuthenticated,
      isLoading,
      user,
      loginWithRedirect,
      getHeldTokens,
      logout,
    };
  }, [isAuthenticated, isLoading, user, loginWithRedirect, getHeldTokens, logout]);
  return <context.Provider value={contextValue}>{children}</context.Provider>;
};
